import { useState, useMemo, useEffect } from "preact/hooks";
import { useTable } from "react-table";
import classNames from "./table.css";
import { DownloadDataButton } from "./DownloadDataButton";

// Create an editable cell renderer
const EditableCell = ({
  value: initialValue,
  row: { index },
  column: { id },
  updateMyData, // This is a custom function that we supplied to our table instance
}) => {
  // We need to keep and update the state of the cell normally
  const [value, setValue] = useState(initialValue);

  const onChange = (e) => {
    setValue(String(e.target.value));
  };

  // We'll only update the external data when the input is blurred
  const onBlur = () => {
    updateMyData(index, id, value);
  };

  // If the initialValue is changed externally, sync it up with our state
  useEffect(() => {
    setValue(initialValue);
  }, [initialValue]);

  return (
    <input
      value={value}
      onChange={onChange}
      onBlur={onBlur}
      inputmode="decimal"
      type="number"
      step="any"
      min="0"
    />
  );
};

// Set our editable cell renderer as the default Cell renderer
const defaultColumn = {
  Cell: EditableCell,
};

const defaultPropGetter = () => ({});

const Table = ({
  columns,
  data,
  updateMyData,
  resetData,
  colorSchemeColors,
}) => {
  // console.log("table data", data);
  // console.log("table columns", columns);
  const {
    getTableProps,
    getTableBodyProps,
    // getHeaderProps = defaultPropGetter,
    getColumnProps = defaultPropGetter,
    // getRowProps = defaultPropGetter,
    getCellProps = defaultPropGetter,
    headerGroups,
    rows,
    prepareRow,
  } = useTable({
    columns,
    data,
    defaultColumn,
    // updateMyData isn't part of the API, but anything we put into these options will automatically be available on the instance.
    // That way we can call this function from our cell renderer!
    updateMyData,
    useControlledState: (state) => {
      return useMemo(
        () => ({
          ...state,
        }),
        [state]
      );
    },
  });

  return (
    <div class="table-wrapper">
      <table {...getTableProps({ class: classNames["edit-table"] })}>
        <thead>
          <tr {...headerGroups[0].getHeaderGroupProps()}>
            {headerGroups[0].headers.map((column) => (
              <th {...column.getHeaderProps()}>{column.render("Header")}</th>
            ))}
          </tr>
          <tr {...headerGroups[1].getHeaderGroupProps()}>
            {headerGroups[1].headers.map((column, i) => (
              <th
                style={{ background: colorSchemeColors[i - 1] }}
                {...column.getHeaderProps()}
              >
                <span style={{ mixBlendMode: "difference", color: "white" }}>
                  {column.render("Header")}
                </span>
              </th>
            ))}
          </tr>
        </thead>
        <tbody {...getTableBodyProps()}>
          {rows.map((row) => {
            prepareRow(row);
            return (
              <tr {...row.getRowProps()}>
                {row.cells.map((cell, i) =>
                  i === 0 ? (
                    <th
                      {...cell.getCellProps([
                        {
                          class: cell.column.class,
                          scope: "row",
                          style: cell.column.style,
                        },
                        getColumnProps(cell.column),
                        getCellProps(cell),
                      ])}
                    >
                      {cell.render("Cell")}
                    </th>
                  ) : (
                    <td
                      {...cell.getCellProps([
                        {
                          class: cell.column.class,
                          style: cell.column.style,
                        },
                        getColumnProps(cell.column),
                        getCellProps(cell),
                      ])}
                    >
                      {cell.render("Cell")}
                    </td>
                  )
                )}
              </tr>
            );
          })}
        </tbody>
      </table>
      <div class="mt-1">
        <button
          class="btn btn-teal mr-4"
          onClick={resetData}
          title="Be careful, you can't undo this!"
        >
          Reset
        </button>
        <DownloadDataButton data={data} />
      </div>
    </div>
  );
};

export { Table };
